import { useState, useEffect } from 'react';
import { scheduledReportAPI } from '../../lib/api';
import { 
  CalendarBlank, 
  Clock,
  Envelope,
  Plus,
  Trash,
  PaperPlaneTilt,
  CheckCircle,
  XCircle,
  Pause,
  Play,
  PencilSimple,
  CaretDown,
  CaretUp,
  FileText,
  Shield,
  CurrencyDollar,
  ChartBar
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function AdminScheduledReportsPage() {
  const [schedules, setSchedules] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [sendingNow, setSendingNow] = useState(null);
  
  const [newSchedule, setNewSchedule] = useState({
    report_type: 'consent-compliance',
    frequency: 'weekly',
    day_of_week: 1,
    day_of_month: 1,
    time_hour: 8,
    recipients: [''],
    format: 'pdf'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [schedulesRes, historyRes] = await Promise.all([
        scheduledReportAPI.getAll(),
        scheduledReportAPI.getHistory(20)
      ]);
      setSchedules(schedulesRes.data.schedules || []);
      setHistory(historyRes.data.history || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load scheduled reports');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSchedule = async () => {
    try {
      // Filter empty recipients
      const recipients = newSchedule.recipients.filter(r => r.trim());
      if (recipients.length === 0) {
        toast.error('At least one recipient email is required');
        return;
      }
      
      const data = { ...newSchedule, recipients };
      await scheduledReportAPI.create(data);
      toast.success('Schedule created');
      setShowCreateModal(false);
      setNewSchedule({
        report_type: 'consent-compliance',
        frequency: 'weekly',
        day_of_week: 1,
        day_of_month: 1,
        time_hour: 8,
        recipients: [''],
        format: 'pdf'
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create schedule');
    }
  };

  const handleToggleActive = async (schedule) => {
    try {
      await scheduledReportAPI.update(schedule.id, { is_active: !schedule.is_active });
      toast.success(schedule.is_active ? 'Schedule paused' : 'Schedule activated');
      loadData();
    } catch (error) {
      toast.error('Failed to update schedule');
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;
    
    try {
      await scheduledReportAPI.delete(scheduleId);
      toast.success('Schedule deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const handleSendNow = async (scheduleId) => {
    setSendingNow(scheduleId);
    try {
      await scheduledReportAPI.sendNow(scheduleId);
      toast.success('Report queued for sending');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send report');
    } finally {
      setSendingNow(null);
    }
  };

  const addRecipient = () => {
    setNewSchedule({ ...newSchedule, recipients: [...newSchedule.recipients, ''] });
  };

  const removeRecipient = (index) => {
    const recipients = newSchedule.recipients.filter((_, i) => i !== index);
    setNewSchedule({ ...newSchedule, recipients: recipients.length ? recipients : [''] });
  };

  const updateRecipient = (index, value) => {
    const recipients = [...newSchedule.recipients];
    recipients[index] = value;
    setNewSchedule({ ...newSchedule, recipients });
  };

  const getReportIcon = (type) => {
    switch (type) {
      case 'consent-compliance': return Shield;
      case 'billing-summary': return CurrencyDollar;
      case 'appointments': return CalendarBlank;
      default: return ChartBar;
    }
  };

  const getReportColor = (type) => {
    switch (type) {
      case 'consent-compliance': return 'bg-teal-100 text-teal-600';
      case 'billing-summary': return 'bg-green-100 text-green-600';
      case 'appointments': return 'bg-blue-100 text-blue-600';
      default: return 'bg-purple-100 text-purple-600';
    }
  };

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Scheduled Reports</h1>
          <p className="text-slate-500">Automatically send reports via email on a schedule</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-teal flex items-center gap-2"
        >
          <Plus size={18} />
          New Schedule
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CalendarBlank size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{schedules.length}</p>
              <p className="text-sm text-slate-500">Total Schedules</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Play size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {schedules.filter(s => s.is_active).length}
              </p>
              <p className="text-sm text-slate-500">Active</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Pause size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {schedules.filter(s => !s.is_active).length}
              </p>
              <p className="text-sm text-slate-500">Paused</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Envelope size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {history.filter(h => h.status === 'sent').length}
              </p>
              <p className="text-sm text-slate-500">Sent (Recent)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Schedules List */}
      <div className="card-clinical overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h3 className="font-semibold text-slate-900">Report Schedules</h3>
        </div>

        {schedules.length === 0 ? (
          <div className="p-8 text-center">
            <CalendarBlank size={48} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No scheduled reports yet</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 text-teal-600 hover:text-teal-700 font-medium"
            >
              Create your first schedule
            </button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {schedules.map((schedule) => {
              const Icon = getReportIcon(schedule.report_type);
              const colorClass = getReportColor(schedule.report_type);
              
              return (
                <div key={schedule.id} className="p-4 hover:bg-slate-50" data-testid={`schedule-${schedule.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg ${colorClass}`}>
                        <Icon size={20} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{schedule.report_name}</p>
                          {!schedule.is_active && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">Paused</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          <Clock size={14} className="inline mr-1" />
                          {schedule.frequency_description}
                        </p>
                        <p className="text-sm text-slate-500">
                          <Envelope size={14} className="inline mr-1" />
                          {schedule.recipients?.join(', ')}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          Format: {schedule.format?.toUpperCase()} | Next: {schedule.next_send ? new Date(schedule.next_send).toLocaleString() : 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleSendNow(schedule.id)}
                        disabled={sendingNow === schedule.id}
                        className="p-2 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors"
                        title="Send now"
                      >
                        {sendingNow === schedule.id ? (
                          <div className="spinner w-4 h-4 border-blue-600" />
                        ) : (
                          <PaperPlaneTilt size={18} />
                        )}
                      </button>
                      <button
                        onClick={() => handleToggleActive(schedule)}
                        className={`p-2 rounded-lg transition-colors ${
                          schedule.is_active 
                            ? 'hover:bg-amber-100 text-amber-600' 
                            : 'hover:bg-green-100 text-green-600'
                        }`}
                        title={schedule.is_active ? 'Pause' : 'Activate'}
                      >
                        {schedule.is_active ? <Pause size={18} /> : <Play size={18} />}
                      </button>
                      <button
                        onClick={() => handleDelete(schedule.id)}
                        className="p-2 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* History Section */}
      <div className="card-clinical overflow-hidden">
        <div 
          className="p-4 border-b border-slate-200 bg-slate-50 cursor-pointer flex items-center justify-between"
          onClick={() => setShowHistory(!showHistory)}
        >
          <h3 className="font-semibold text-slate-900">Send History ({history.length})</h3>
          {showHistory ? <CaretUp size={20} /> : <CaretDown size={20} />}
        </div>

        {showHistory && (
          history.length === 0 ? (
            <div className="p-8 text-center">
              <FileText size={48} className="text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No reports sent yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
              {history.map((item) => (
                <div key={item.id} className="p-3 hover:bg-slate-50 text-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {item.status === 'sent' ? (
                        <CheckCircle size={16} className="text-green-500" />
                      ) : (
                        <XCircle size={16} className="text-red-500" />
                      )}
                      <span className="font-medium">{item.report_type?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                      <span className="text-slate-400">→</span>
                      <span className="text-slate-500">{item.recipients?.slice(0, 2).join(', ')}{item.recipients?.length > 2 ? '...' : ''}</span>
                    </div>
                    <span className="text-slate-400 text-xs">
                      {item.sent_at ? new Date(item.sent_at).toLocaleString() : 'N/A'}
                    </span>
                  </div>
                  {item.error && (
                    <p className="text-red-500 text-xs mt-1 ml-7">{item.error}</p>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-semibold text-slate-900">Create Report Schedule</h2>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Report Type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Report Type</label>
                <select
                  value={newSchedule.report_type}
                  onChange={(e) => setNewSchedule({ ...newSchedule, report_type: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                >
                  <option value="consent-compliance">Consent Compliance Report</option>
                  <option value="billing-summary">Billing Summary Report</option>
                  <option value="appointments">Appointment Report</option>
                  <option value="admin-summary">Administrative Summary</option>
                </select>
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Frequency</label>
                <select
                  value={newSchedule.frequency}
                  onChange={(e) => setNewSchedule({ ...newSchedule, frequency: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              {/* Day of Week (for weekly) */}
              {newSchedule.frequency === 'weekly' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Day of Week</label>
                  <select
                    value={newSchedule.day_of_week}
                    onChange={(e) => setNewSchedule({ ...newSchedule, day_of_week: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  >
                    {days.map((day, i) => (
                      <option key={i} value={i}>{day}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Day of Month (for monthly) */}
              {newSchedule.frequency === 'monthly' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Day of Month (1-28)</label>
                  <input
                    type="number"
                    min="1"
                    max="28"
                    value={newSchedule.day_of_month}
                    onChange={(e) => setNewSchedule({ ...newSchedule, day_of_month: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              )}

              {/* Time */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Time (Hour, UTC)</label>
                <select
                  value={newSchedule.time_hour}
                  onChange={(e) => setNewSchedule({ ...newSchedule, time_hour: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                >
                  {[...Array(24)].map((_, i) => (
                    <option key={i} value={i}>{i.toString().padStart(2, '0')}:00 UTC</option>
                  ))}
                </select>
              </div>

              {/* Format */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Format</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="pdf"
                      checked={newSchedule.format === 'pdf'}
                      onChange={(e) => setNewSchedule({ ...newSchedule, format: e.target.value })}
                      className="text-teal-600 focus:ring-teal-500"
                    />
                    <span>PDF</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="csv"
                      checked={newSchedule.format === 'csv'}
                      onChange={(e) => setNewSchedule({ ...newSchedule, format: e.target.value })}
                      className="text-teal-600 focus:ring-teal-500"
                    />
                    <span>CSV</span>
                  </label>
                </div>
              </div>

              {/* Recipients */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Recipients</label>
                {newSchedule.recipients.map((email, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => updateRecipient(index, e.target.value)}
                      placeholder="email@example.com"
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                    {newSchedule.recipients.length > 1 && (
                      <button
                        onClick={() => removeRecipient(index)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        <Trash size={18} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={addRecipient}
                  className="text-teal-600 hover:text-teal-700 text-sm font-medium"
                >
                  + Add another recipient
                </button>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSchedule}
                className="btn-teal"
              >
                Create Schedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
